import React, { useContext, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    HiCheckCircle,
    HiExclamationCircle,
    HiOutlineChevronDown,
    HiOutlineChevronUp,
    HiOutlinePhotograph,
    HiOutlineX,
} from 'react-icons/hi';
import { TbLoader2 } from 'react-icons/tb';
import { Progress } from 'components/ui';
import MySocialIcon from 'components/shared/MySocialIcon';
import { BulkProgressContext } from 'contexts/BulkProgressContext';

const STATUS_LABELS = {
    queued: 'Queued',
    downloading: 'Downloading media…',
    ready: 'Ready',
    failed: 'Failed',
    timeout: 'Timed out',
};

// A row that lost some of its media still finishes (ready) or keeps waiting on the rest
// (downloading, possibly for hours): the count goes on the row, not only in its tooltip.
const getRowStatusLabel = (row) => {
    const failed = Number(row?.failedCount) || 0;
    if (failed > 0 && row.status === 'ready') {
        const expected = Math.max(row.media_urls?.length || 0, failed);
        return `Ready, ${failed} of ${expected} media file${expected === 1 ? '' : 's'} failed`;
    }
    if (failed > 0 && row.status === 'downloading') {
        return `Downloading media, ${failed} failed`;
    }
    return STATUS_LABELS[row?.status] || row?.status;
};

const RowStatusIcon = ({ status }) => {
    if (status === 'ready') {
        return (
            <motion.div
                key='ready'
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            >
                <HiCheckCircle className='w-6 h-6 text-emerald-500' />
            </motion.div>
        );
    }
    if (status === 'failed' || status === 'timeout') {
        return (
            <motion.div
                key='error'
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            >
                <HiExclamationCircle className='w-6 h-6 text-red-500' />
            </motion.div>
        );
    }
    if (status === 'downloading') {
        return (
            <motion.div key='dl' initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <TbLoader2 className='w-5 h-5 text-primary-500 animate-spin' />
            </motion.div>
        );
    }
    return (
        <motion.div
            key='queued'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='relative w-2.5 h-2.5'
        >
            <span className='absolute inset-0 rounded-full bg-gray-300 dark:bg-gray-600' />
            <span className='absolute inset-0 rounded-full bg-gray-400 dark:bg-gray-500 animate-ping opacity-60' />
        </motion.div>
    );
};

const RowThumbnail = ({ thumbnail, status }) => {
    if (thumbnail) {
        return (
            <div className='relative w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800'>
                <img
                    src={thumbnail}
                    alt=''
                    className={`w-full h-full object-cover transition-all duration-500 ${
                        status === 'ready' ? '' : 'opacity-70'
                    }`}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
                {status !== 'ready' && status !== 'failed' && status !== 'timeout' && (
                    <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent' />
                )}
            </div>
        );
    }
    return (
        <div className='w-12 h-12 shrink-0 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/30 flex items-center justify-center text-primary-600 dark:text-primary-300'>
            <HiOutlinePhotograph className='w-6 h-6' />
        </div>
    );
};

const RowItem = ({ row }) => {
    const statusLabel = getRowStatusLabel(row);
    const isError = row.status === 'failed' || row.status === 'timeout';
    const hasFailedMedia = !isError && Number(row.failedCount) > 0;
    return (
        <motion.li
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className='flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-200 dark:hover:border-gray-700 transition-colors'
        >
            <RowThumbnail thumbnail={row.thumbnail} status={row.status} />
            <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-tertiary-900 dark:text-gray-100 truncate'>
                    {row.content || <span className='italic text-gray-400'>No content</span>}
                </p>
                <div className='flex items-center gap-2 mt-1'>
                    {row.networks?.length > 0 && (
                        <div className='flex -space-x-1'>
                            {row.networks.slice(0, 4).map((network) => (
                                <MySocialIcon
                                    key={network}
                                    network={network}
                                    className='!w-4 !h-4'
                                    wrapperClassName='!w-4 !h-4 ring-2 ring-white dark:ring-gray-900 rounded-full'
                                />
                            ))}
                            {row.networks.length > 4 && (
                                <span className='text-[10px] text-gray-500 ml-1.5'>
                                    +{row.networks.length - 4}
                                </span>
                            )}
                        </div>
                    )}
                    <span
                        className={`text-xs ${
                            isError
                                ? 'text-red-500'
                                : hasFailedMedia
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : row.status === 'ready'
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : 'text-gray-500 dark:text-gray-400'
                        }`}
                        title={isError || hasFailedMedia ? row.errorMessage || statusLabel : statusLabel}
                    >
                        {statusLabel}
                    </span>
                </div>
            </div>
            <div className='shrink-0'>
                <AnimatePresence mode='wait'>
                    <RowStatusIcon status={row.status} />
                </AnimatePresence>
            </div>
        </motion.li>
    );
};

const BulkProgressModal = () => {
    const { rows, isCollapsed, toggleCollapse, closeBulkProgress } = useContext(BulkProgressContext);

    const { total, readyCount, failedCount, allDone, percent } = useMemo(() => {
        const total = rows.length;
        const readyCount = rows.filter((r) => r.status === 'ready').length;
        const failedCount = rows.filter((r) => r.status === 'failed' || r.status === 'timeout').length;
        const terminal = readyCount + failedCount;
        const allDone = total > 0 && terminal === total;
        const percent = total === 0 ? 0 : Math.round((terminal / total) * 100);
        return { total, readyCount, failedCount, allDone, percent };
    }, [rows]);

    if (rows.length === 0) return null;

    const headerClass = allDone
        ? failedCount > 0
            ? 'bg-gradient-to-r from-amber-500 to-orange-500'
            : 'bg-gradient-to-r from-emerald-500 to-green-500'
        : 'bg-gradient-primary';

    const titleText = allDone
        ? failedCount > 0
            ? `${readyCount} ready · ${failedCount} failed`
            : `All ${readyCount} posts ready`
        : `Processing ${total} post${total === 1 ? '' : 's'}`;

    return (
        <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className='fixed right-0 bottom-0 lg:right-5 lg:bottom-5 w-full lg:w-[440px] rounded-t-2xl lg:rounded-2xl shadow-2xl bg-white dark:bg-gray-900 overflow-hidden z-[999990000000000] border border-gray-100 dark:border-gray-800'
        >
            <div className={`${headerClass} text-white px-5 py-4 transition-colors duration-500`}>
                <div className='flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-3 min-w-0'>
                        <AnimatePresence mode='wait'>
                            {allDone ? (
                                <motion.div
                                    key='done'
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                                >
                                    <HiCheckCircle className='w-6 h-6' />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key='loading'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                >
                                    <TbLoader2 className='w-5 h-5 animate-spin' />
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <div className='min-w-0'>
                            <p className='text-sm font-semibold truncate'>{titleText}</p>
                            <p className='text-[11px] text-white/80 truncate'>
                                {allDone
                                    ? 'Closing in a moment…'
                                    : `${readyCount} / ${total} ready`}
                            </p>
                        </div>
                    </div>
                    <div className='flex items-center gap-1 shrink-0'>
                        <button
                            type='button'
                            onClick={toggleCollapse}
                            className='p-1.5 rounded-lg hover:bg-white/15 transition-colors'
                            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                            {isCollapsed ? (
                                <HiOutlineChevronUp className='w-4 h-4' />
                            ) : (
                                <HiOutlineChevronDown className='w-4 h-4' />
                            )}
                        </button>
                        <button
                            type='button'
                            onClick={closeBulkProgress}
                            className='p-1.5 rounded-lg hover:bg-white/15 transition-colors'
                            aria-label='Close'
                        >
                            <HiOutlineX className='w-4 h-4' />
                        </button>
                    </div>
                </div>
                <div className='mt-3'>
                    <Progress
                        percent={percent}
                        showInfo={false}
                        color='white'
                    />
                </div>
            </div>

            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        key='body'
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className='overflow-hidden'
                    >
                        <ul className='flex flex-col gap-2 p-3 max-h-[360px] overflow-y-auto'>
                            <AnimatePresence initial={false}>
                                {rows.map((row) => (
                                    <RowItem key={row.key} row={row} />
                                ))}
                            </AnimatePresence>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default BulkProgressModal;
